import { useState, useEffect, useRef } from "react";
import { Icon } from "@iconify/react";
import { useNavigate } from "react-router-dom";
import { useQueryParams } from "../../../contexts/queryParamsContext";
import { useAuth } from "../../../contexts/auth/authContext";
import Common from "../../../helper/common";
import {
  BookingModeEnum,
  BookingStatusEnum,
  PaymentStatusEnum,
} from "../../../constants/appConstant";
import { Button } from "../../../components/ui/button";
import * as yup from "yup";
import makeAnimated from "react-select/animated";
import AdvanceTable from "../../../components/advanceTable";
import { Formik } from "formik";
import {
  FormikInput,
  FormikMultiSelect,
  FormikSearchSelect,
} from "../../../components/formik-components";

const BookingReport = () => {
  const navigate = useNavigate();
  const { params, setParams } = useQueryParams();
  const { user: authUser } = useAuth();
  const [data, setData] = useState({});
  const [fromDate, setFromDate] = useState(null);
  const [tillDate, setTillDate] = useState(null);
  const [sportList, setSportList] = useState([]);
  const [playerList, setPlayerList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [availableBookingModes, setAvailableBookingModes] = useState([]);

  // Set default dates - from: 1st of current month, till: today
  const firstDayOfCurrentMonth = Common.moment
    .utc()
    .startOf("month")
    .format("YYYY-MM-DD");
  const today = Common.moment.utc().format("YYYY-MM-DD");

  // Calculate 3 months ago for restriction
  const threeMonthsAgo = Common.moment
    .utc()
    .subtract(3, "months")
    .format("YYYY-MM-DD");

  const formInitialValue = {
    fromDate: firstDayOfCurrentMonth,
    tillDate: today,
    bookingModes: [],
    sportIds: [],
    playerIds: [],
    bookingStatuses: [],
    paymentStatuses: [],
  };

  const formValidations = yup.object({});
  const formikRef = useRef();
  const animatedComponents = makeAnimated();

  const onSubmitData = async (values, actions) => {
    await getData();
    actions.setSubmitting(false);
  };

  useEffect(() => {
    const fetchData = async () => {
      getAvailableBookingModes();
      await getSports();
      await getPlayers();
      // Make default API call with date filters
      setTimeout(() => {
        getData();
      }, 500);
    };
    fetchData();
  }, [authUser]);

  // Auto-fetch data when filters change (handled by individual onChange handlers)

  const resetFilters = () => {
    if (formikRef.current) {
      Object.keys(formInitialValue).forEach((key) => {
        formikRef.current.setFieldValue(key, formInitialValue[key] ?? "");
      });
    }
    setData({});
    setLoading(false);
    // Call API after resetting to show data with default date filters
    setTimeout(() => {
      getData();
    }, 100);
  };

  const columns = [
    { title: "Booking ID", type: "text", isVisible: false, key: "bookingId" },

    { title: "Ref No", type: "text", isVisible: true, key: "refNo" },
    { title: "Player Name", type: "text", isVisible: true, key: "playerName" },
    {
      title: "Player Phone",
      type: "text",
      isVisible: true,
      key: "playerPhone",
    },
    // { title: "Club", type: "text", isVisible: true, key: "clubName" },
    { title: "Sport", type: "text", isVisible: true, key: "sportName" },
    { title: "Court", type: "text", isVisible: true, key: "courtName" },
    {
      title: "Booking Date",
      type: "text",
      isVisible: true,
      key: "bookingDate",
    },
    // { title: "Start Time", type: "text", isVisible: true, key: "startTime" },
    // { title: "End Time", type: "text", isVisible: true, key: "endTime" },
    {
      title: "Booking Detail",
      type: "text",
      isVisible: true,
      key: "bookingDetail",
    },
    {
      title: "Booking Mode",
      type: "text",
      isVisible: true,
      key: "bookingMode",
    },
    {
      title: "Booking Status",
      type: "text",
      isVisible: true,
      key: "bookingStatus",
    },
    {
      title: "Payment Status",
      type: "text",
      isVisible: true,
      key: "paymentStatus",
        // render: (row) => getPaymentStatusLabel(row.paymentStatus),
        render: (row) => Common.Utility.renderBadge(row.paymentStatusId,row.paymentStatus),
    },
    {
      title: "Peak Type",
      type: "text",
      isVisible: true,
      key: "peakType",
    },
    {
      title: "Court Charges",
      type: "number",
      isVisible: true,
      key: "courtcharges",
      showTotal: true,
    },
    {
      title: "Discount Amount",
      type: "number",
      isVisible: true,
      key: "discountAmount",
      showTotal: true,
    },
    {
      title: "Platform Fee",
      type: "number",
      isVisible: true,
      key: "platformfee",
      showTotal: true,
    },
    {
      title: "Total Amount",
      type: "number",
      isVisible: true,
      key: "total",
      showTotal: true,
    },
    {
      title: "Received Amount",
      type: "number",
      isVisible: true,
      key: "receivedAmount",
      showTotal: true,
    },
    {
      title: "Remaining Amount",
      type: "number",
      isVisible: true,
      key: "remainingAmount",
      showTotal: true,
    },

    {
      title: "Duration (mins)",
      type: "number",
      isVisible: true,
      key: "duration",
      showTotal: true,
    },
    {
      title: "No of Players",
      type: "number",
      isVisible: true,
      key: "noofplayers",
    },
    {
      title: "Bank Discounts Details",
      type: "text",
      isVisible: true,
      key: "bankDiscounts",
    },
    {
      title: "Created By",
      type: "text",
      isVisible: true,
      key: "createdByName",
    },
     {
      title: "Created On",
      type: "text",
      isVisible: true,
      key: "createdOn",
    },
     {
      title: "Updated By",
      type: "text",
      isVisible: true,
      key: "updatedByName",
    },
     {
      title: "Updated On",
      type: "text",
      isVisible: true,
      key: "updatedOn",
    },
     {
      title: "Cancelled By",
      type: "text",
      isVisible: true,
      key: "cancelledByName",
    },
     {
      title: "Cancelled On",
      type: "text",
      isVisible: true,
      key: "cancelledon",
    },
    {
      title: "Actions",
      type: "text",
      isVisible: true,
      key: "actions",
      render: (row) => (
        <button
          onClick={() => navigateToBookingDetails(row.bookingId)}
          className="text-blue-600 hover:text-blue-800 transition-colors"
          title="View Booking Details"
        >
          <Icon icon="heroicons:eye" className="w-5 h-5" />
        </button>
      ),
    },
  ];

  const getAvailableBookingModes = () => {
    if (authUser?.myClubs && authUser.defaultClubId) {
      const myClub = authUser.myClubs.find(
        (item) => item.id === authUser.defaultClubId
      );
      if (myClub) {
        const bookingModes = [
          { label: "All", value: null },
          { label: "App Booking", value: BookingModeEnum.App_Booking },
          { label: "Club Booking", value: BookingModeEnum.Club_Booking },
        ];

        // Add conditional booking modes based on club settings
        if (myClub.isownapp) {
          bookingModes.push({
            label: "My App Booking",
            value: BookingModeEnum.My_App_Booking,
          });
        }
        if (myClub.isownweb) {
          bookingModes.push({
            label: "Web Booking",
            value: BookingModeEnum.Web_Booking,
          });
        }
        if (myClub.isownwhatsapp) {
          bookingModes.push({
            label: "Whatsapp Booking",
            value: BookingModeEnum.Whatsapp_Booking,
          });
        }

        setAvailableBookingModes(bookingModes);
      }
    }
  };

  const getSports = async () => {
    try {
      const response = await Common.ApiService.getInstance().request(
        "GetClubSports"
      );
      const sportsData = [
        { id: null, name: "All Sports", value: null, label: "All Sports" },
        ...(response?.data ?? []).map((sport) => ({
          id: sport.id,
          name: sport.name,
          value: sport.id,
          label: sport.name,
        })),
      ];
      setSportList(sportsData);
    } catch (error) {
      console.error("Error fetching sports:", error);
    }
  };

  const getPlayers = async () => {
    try {
      const response = await Common.ApiService.getInstance().request(
        "ClubPlayersddl"
      );
      const playersData = [
        { id: null, name: "All Players", value: null, label: "All Players" },
        ...(response?.data ?? []).map((player) => ({
          id: player.id,
          name: player.name,
          value: player.id,
          label: player.name,
        })),
      ];
      setPlayerList(playersData);
    } catch (error) {
      console.error("Error fetching players:", error);
    }
  };

  const getData = async () => {
    setLoading(true);
    setData({});
    const values = formikRef.current.values;

    if (fromDate && tillDate && fromDate > tillDate) {
      Common.showToast(
        "End Date must be greater than Start Date",
        "Error",
        "Info"
      );
      setLoading(false);
      return;
    }

    const requestData = {
      fromDate: values.fromDate || null,
      tillDate: values.tillDate || null,
      bookingModes:
        values.bookingModes?.length > 0
          ? values.bookingModes
              .map((x) => x.value)
              .filter((val) => val !== null)
          : null,
      sportIds:
        values.sportIds?.length > 0
          ? values.sportIds.map((x) => x.value).filter((val) => val !== null)
          : null,
      playerIds:
        values.playerIds?.length > 0
          ? values.playerIds.map((x) => x.value).filter((val) => val !== null)
          : null,
      bookingStatuses:
        values.bookingStatuses?.length > 0
          ? values.bookingStatuses
              .map((x) => x.value)
              .filter((val) => val !== null)
          : null,
      paymentStatuses:
        values.paymentStatuses?.length > 0
          ? values.paymentStatuses
              .map((x) => x.value)
              .filter((val) => val !== null)
          : null,
    };

    console.log("Request Data", requestData);

    try {
      const response = await Common.ApiService.getInstance().request(
        "GetBookingReport",
        requestData,
        "Post"
      );

      if (response?.data && response.data.length > 0) {
        const processedData = response.data.map((row) => ({
          ...row,
          bookingId: row.bookingId || "-",
          refNo: row.refNo || "-",
          playerName: row.playerName || "-",
          playerPhone: row.playerPhone || "-",
          //   clubName: row.clubName || "-",
          sportName: row.sportName || "-",
          courtName: row.courtName || "-",
          bookingDate: row.bookingDate
            ? Common.moment(row.bookingDate).format("DD-MM-YYYY")
            : "-",
          startTime: row.startDateTime
            ? Common.moment(row.startDateTime).format("HH:mm")
            : "-",
          endTime: row.endDateTime
            ? Common.moment(row.endDateTime).format("HH:mm")
            : "-",
          bookingMode: getBookingModeLabel(row.bookingMode),
          bookingStatus: getBookingStatusLabel(row.bookingStatus),
          // paymentStatus: getPaymentStatusLabel(row.paymentStatus),
          paymentStatusId: row.paymentStatus,
          paymentStatus: Common.Utility.getKeyByValue(PaymentStatusEnum,row.paymentStatus),
          peakType: row.peakType || "-",
          total: parseFloat(row.total) || 0,
          receivedAmount: parseFloat(row.receivedAmount) || 0,
          remainingAmount: parseFloat(row.remainingAmount) || 0,
          courtcharges: parseFloat(row.courtcharges) || 0,
          discountAmount: parseFloat(row.discountAmount) || 0,
          platformfee: parseFloat(row.platformfee) || 0,
          duration: parseInt(row.duration) || 0,
          noofplayers: parseInt(row.noofplayers) || 0,
          bankDiscounts: formatBankDiscounts(row.bankDiscounts),
          createdByName:row.createdByName??"-",
          createdOn:row.createdOn? Common.moment(row.createdOn).format("DD-MM-YYYY"):"-",
          updatedByName:row.updatedByName??"-",
          updatedOn:row.updatedOn? Common.moment(row.updatedOn).format("DD-MM-YYYY"):"-",
          cancelledByName:row.cancelledByName??"-",
          cancelledon:row.cancelledon? Common.moment(row.cancelledon).format("DD-MM-YYYY"):"-",

        }));

        let obj = {
          ...response,
          data: processedData,
        };
        setData(obj);
      } else {
        setData({ data: [] });
      }
    } catch (error) {
      console.error("Error fetching booking report:", error);
      Common.showToast("Error fetching booking report data", "Error", "Error");
    } finally {
      setLoading(false);
    }
  };

  const getBookingModeLabel = (mode) => {
    if (!mode) return "-";
    return Common.Utility.getKeyByValue(BookingModeEnum, mode) || "-";
  };

  const getBookingStatusLabel = (status) => {
    if (!status) return "-";
    return Common.Utility.getKeyByValue(BookingStatusEnum, status) || "-";
  };

  const getPaymentStatusLabel = (status) => {
    if (!status) return "-";

    // Convert to number if it's a string
    const numericStatus =
      typeof status === "string" ? parseInt(status) : status;

    switch (numericStatus) {
      case PaymentStatusEnum.Paid: // 1
        return (
          <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
            Paid
          </span>
        );
      case PaymentStatusEnum.Un_Paid: // 3
        return (
          <span className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm font-medium">
            Unpaid
          </span>
        );
      case PaymentStatusEnum.Partial_Paid: // 2
        return (
          <span className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-sm font-medium">
            Partial
          </span>
        );
      default:
        return (
          <span className="bg-gray-100 text-gray-800 px-3 py-1 rounded-full text-sm font-medium">
            Unknown
          </span>
        );
    }
  };

  const navigateToBookingDetails = (bookingId) => {
    if (bookingId) {
      setParams({
        ...params,
        bookingId: bookingId,
      });
      navigate("/app/bookingDetails");
    }
  };

  const formatBankDiscounts = (bankDiscountsJson) => {
    try {
      if (!bankDiscountsJson || bankDiscountsJson === "[]") return "-";

      const discounts = JSON.parse(bankDiscountsJson);
      if (!Array.isArray(discounts) || discounts.length === 0) return "-";

      // return discounts
      //   .map((discount) => {
      //     return `${
      //       discount.issuerName || discount.bankDiscountName || "Unknown"
      //     }: ${discount.promoCodeAmt || discount.discountAmt || 0}`;
      //   })
      //   .join(", ");
      return discounts
      .map((discount) => {
        return `[${discount.issuerName || "Unknown"} , ${discount.promoCodeAmt || discount.discountAmt || 0} , ${discount.bankDiscountName || "-"} , ${discount.bin_number || "-"}]`;
      })
      .join(", ");

    } catch (error) {
      console.error("Error parsing bank discounts:", error);
      return "-";
    }
  };

  return (
    <Formik
      initialValues={formInitialValue}
      validationSchema={formValidations}
      innerRef={formikRef}
      validateOnBlur={false}
      validateOnChange={false}
      onSubmit={onSubmitData}
    >
      {(formikProps) => {
        formikRef.current = formikProps;
        return (
          <>
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <h2 className="text-xl font-semibold mb-6 text-gray-800">
                Booking Report Filters
              </h2>

              <div className="space-y-6">
                {/* Date Range, Payment Status and Booking Status Row */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <FormikInput
                    formikProps={formikProps}
                    label={"From Date"}
                    value={formikProps.values.fromDate}
                    onChange={(e) => {
                      const selectedStartDate = e;

                      // Check 3-month restriction
                      if (selectedStartDate < threeMonthsAgo) {
                        Common.showToast(
                          "Date cannot be more than 3 months ago",
                          "Error",
                          "info"
                        );
                        setFromDate(null);
                        formikProps.setFieldValue("fromDate", null);
                        return;
                      }

                      if (tillDate && selectedStartDate > tillDate) {
                        Common.showToast(
                          "Start Date must be lesser than End Date",
                          "Error",
                          "info"
                        );
                        setFromDate(null);
                        formikProps.setFieldValue("fromDate", null);
                      } else {
                        setFromDate(selectedStartDate);
                        formikProps.setFieldValue(
                          "fromDate",
                          selectedStartDate
                        );
                      }
                    }}
                    type={"date"}
                    name={"fromDate"}
                    min={threeMonthsAgo}
                    max={today}
                    clearable={false}
                    className=""
                  />

                  <FormikInput
                    formikProps={formikProps}
                    label={"Till Date"}
                    value={formikProps.values.tillDate}
                    onChange={(e) => {
                      const selectedEndDate = e;

                      // Check 3-month restriction
                      if (selectedEndDate < threeMonthsAgo) {
                        Common.showToast(
                          "Date cannot be more than 3 months ago",
                          "Error",
                          "info"
                        );
                        setTillDate(null);
                        formikProps.setFieldValue("tillDate", null);
                        return;
                      }

                      if (selectedEndDate < fromDate) {
                        Common.showToast(
                          "End Date must be greater",
                          "Error",
                          "info"
                        );
                        setTillDate("");
                        formikProps.setFieldValue("tillDate", null);
                      } else {
                        setTillDate(selectedEndDate);
                        formikProps.setFieldValue("tillDate", selectedEndDate);
                      }
                    }}
                    type={"date"}
                    name={"tillDate"}
                    min={threeMonthsAgo}
                    max={today}
                    clearable={false}
                    className=""
                  />

                  <FormikMultiSelect
                    formikProps={formikProps}
                    animatedComponents={animatedComponents}
                    label="Payment Status"
                    name={"paymentStatuses"}
                    data={[
                      { label: "All", value: null },
                      ...Object.entries(PaymentStatusEnum).map(
                        ([key, value]) => ({
                          label: key.replace(/_/g, " "),
                          value: value,
                        })
                      ),
                    ]}
                    value={formikProps.values.paymentStatuses}
                    onChange={(val) => {
                      formikProps.setFieldValue("paymentStatuses", val);
                      setTimeout(() => {
                        if (val && val.length > 0) {
                          getData();
                        }
                      }, 100);
                    }}
                    className=""
                  />

                  <FormikMultiSelect
                    formikProps={formikProps}
                    animatedComponents={animatedComponents}
                    label="Booking Status"
                    name={"bookingStatuses"}
                    data={[
                      { label: "All", value: null },
                      { label: "Booked", value: BookingStatusEnum.Booked },
                      {
                        label: "Cancelled by Club",
                        value: BookingStatusEnum.Cancelled_By_Club,
                      },
                    ]}
                    value={formikProps.values.bookingStatuses}
                    onChange={(val) => {
                      formikProps.setFieldValue("bookingStatuses", val);
                      setTimeout(() => {
                        if (val && val.length > 0) {
                          getData();
                        }
                      }, 100);
                    }}
                    className=""
                  />
                </div>

                {/* Filter Row 1 */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <FormikMultiSelect
                    formikProps={formikProps}
                    animatedComponents={animatedComponents}
                    label="Booking Modes"
                    name={"bookingModes"}
                    data={availableBookingModes}
                    value={formikProps.values.bookingModes}
                    onChange={(val) => {
                      formikProps.setFieldValue("bookingModes", val);
                      setTimeout(() => {
                        if (val && val.length > 0) {
                          getData();
                        }
                      }, 100);
                    }}
                    className=""
                  />

                  <FormikMultiSelect
                    formikProps={formikProps}
                    animatedComponents={animatedComponents}
                    label="Sports"
                    name={"sportIds"}
                    data={sportList}
                    value={formikProps.values.sportIds}
                    onChange={(val) => {
                      formikProps.setFieldValue("sportIds", val);
                      setTimeout(() => {
                        if (val && val.length > 0) {
                          getData();
                        }
                      }, 100);
                    }}
                    className=""
                  />

                  <FormikMultiSelect
                    formikProps={formikProps}
                    animatedComponents={animatedComponents}
                    label="Players"
                    name={"playerIds"}
                    data={playerList}
                    value={formikProps.values.playerIds}
                    onChange={(val) => {
                      formikProps.setFieldValue("playerIds", val);
                      setTimeout(() => {
                        if (val && val.length > 0) {
                          getData();
                        }
                      }, 100);
                    }}
                    className=""
                  />

                  {/* Action Buttons */}
                  <div className="flex space-x-3">
                    <Button
                      className="px-4 h-10 bg-gray-600 hover:bg-gray-700 text-white text-sm font-medium"
                      onClick={resetFilters}
                    >
                      Reset Filter
                    </Button>
                    <Button
                      className="px-4 h-10 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium disabled:opacity-50"
                      onClick={getData}
                      disabled={loading}
                    >
                      {loading ? "Loading..." : "Fetch Data"}
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {loading && (
              <div className="mt-6 bg-white p-8 rounded-lg shadow-sm border text-center">
                <div className="text-blue-600 text-lg">
                  Loading booking data...
                </div>
                <div className="text-gray-400 text-sm mt-2">
                  Please wait while we fetch the latest information.
                </div>
              </div>
            )}

            {!loading && data?.data && data.data.length > 0 && (
              <div className="mt-6">
                <AdvanceTable
                  title={"Booking Report Results"}
                  columns={columns.filter((item) => item.isVisible)}
                  data={data}
                  getData={getData}
                  showPagination={false}
                  showExportoptions={true}
                  exportFileName="BookingReport"
                />
              </div>
            )}

            {!loading && data?.data && data.data.length === 0 && (
              <div className="mt-6 bg-white p-8 rounded-lg shadow-sm border text-center">
                <div className="text-gray-500 text-lg">
                  No booking data found for the selected filters.
                </div>
                <div className="text-gray-400 text-sm mt-2">
                  Try adjusting your date range or filter criteria.
                </div>
              </div>
            )}
          </>
        );
      }}
    </Formik>
  );
};

export default BookingReport;
